<template>
    <div v-if="isLoading">
        <content-loader :width="width" height="150">
            <circle cx="50" cy="60" r="11" />
            <rect x="78" y="32" rx="4" ry="4" width="3500" height="52" />
            <circle cx="50" cy="122" r="11" />
            <rect x="78" y="96" rx="4" ry="4" width="3500" height="52" />
        </content-loader>
    </div>
    <table v-else class="interlocuteur-table">
        <thead>
            <tr>
                <th />
                <th>
                    <span>
                        {{ $t('contact.page.contact.select.table.role') }}
                    </span>
                    <div class="sortIcons" @click="doSortRole()">
                        <img v-if="this.isSortUp(sortRole)" :src="sortUpIcon" alt="sort up icon" class="pointer"/>
                        <img v-if="this.isSortDown(sortRole)" :src="sortDownIcon" alt="sort down icon" class="pointer"/>
                    </div>
                </th>
                <th>
                    <span>
                        {{ $t('contact.page.contact.select.table.name') }}
                    </span>
                    <div class="sortIcons" @click="doSortNom()">
                        <img v-if="this.isSortUp(sortNom)" :src="sortUpIcon" alt="sort up icon" class="pointer"/>
                        <img v-if="this.isSortDown(sortNom)" :src="sortDownIcon" alt="sort down icon" class="pointer"/>
                    </div>
                </th>
                <th>
                    <span>
                        {{ $t('contact.page.contact.select.table.phone') }}
                    </span>
                </th>
                <th>
                    <span>
                        {{ $t('contact.page.contact.select.table.email') }}
                    </span>
                </th>
            </tr>
        </thead>
        <tbody v-if="interlocuteurs.length > 0">
            <tr v-on:click="toggleSelect(interlocuteur)" v-for="interlocuteur in interlocuteurs" :key="interlocuteur.id">
                <td>
                    <RadioBox class="radio" :selected="interlocuteur.id === selectedInterlocuteur?.id" />
                </td>
                <td class="item-role">
                    {{ formatRoles(interlocuteur) }}
                </td>
                <td class="item-name">
                    {{ formatName(interlocuteur) }}
                </td>
                <td class="item-phone">
                    {{ interlocuteur.telephone }}
                </td>
                    <td class="item-email">
                    {{ interlocuteur.email }}
                </td>
            </tr>
        </tbody>
        <tbody v-else>
            <tr>
                <td class="notFoundTd" colspan="5">
                    {{ $t('contact.page.contact.select.table.noresult') }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import { getRolesInterlocuteur, Interlocuteur } from '@/models/Interlocuteur';
import RadioBox from '@/components/ui-components/RadioBox.vue';
import { defineComponent, PropType } from 'vue';
import { ContentLoader } from 'vue-content-loader';
import { nextStatus, SortType } from '@/models/SortType';

export default defineComponent({
    data() {
        return {
            sortRole: SortType.NONE,
            sortNom: SortType.NONE,
            SortType,
        };
    },
    props: {
        isLoading: {type: Boolean},
        interlocuteurs: { type: Object, required: true },
        selectedInterlocuteur: { type: Object as PropType<Interlocuteur> },
        width: {type: Number},
    },
    components: {
        RadioBox, ContentLoader
    },
    computed: {
        sortUpIcon() {
            return require('@/assets/img/sort-up.svg');
        },
        sortDownIcon() {
            return require('@/assets/img/sort-down.svg');
        },
    },
    methods: {
        toggleSelect(interlocuteur: Interlocuteur) {
            const newSelection = interlocuteur.id === this.selectedInterlocuteur?.id ? undefined : interlocuteur;
            this.$emit('pick', newSelection);
        },
        formatRoles(interlocuteur: Interlocuteur) {
            const rolesMap = getRolesInterlocuteur();
            return interlocuteur.roles.map(role => rolesMap.get(role)).join(', ');
        },
        formatName(interlocuteur: Interlocuteur) {
            const values = [interlocuteur.prenom, interlocuteur.nom].filter(v => v !== undefined);
            return values.join(' ');
        },
        isSortUp(sortStatus: SortType) {
            return sortStatus === SortType.ASC || sortStatus === SortType.NONE;
        },
        isSortDown(sortStatus: SortType) {
            return sortStatus === SortType.DESC || sortStatus === SortType.NONE;
        },
        doSortRole() {
            this.sortNom = SortType.NONE;
            this.sortRole = nextStatus(this.sortRole);
            this.$emit('sort', {
                sortData: 'role',
                sortStatus: this.sortRole,
            });
        },
        doSortNom() {
            this.sortRole = SortType.NONE;
            this.sortNom = nextStatus(this.sortNom);
            this.$emit('sort', {
                sortData: 'nom',
                sortStatus: this.sortNom,
            });
        },
    },
});
</script>

<style scoped>
.interlocuteur-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    text-align: left;
}

.interlocuteur-table thead {
    position: sticky;
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
    font-weight: 600;
}

.interlocuteur-table thead th {
    box-sizing: border-box;
    padding: 0;
    position: sticky;
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 52px;
}

.interlocuteur-table thead th:first-child {
    width: 72px;
}

.interlocuteur-table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 52px;
}

.interlocuteur-table tbody {
    cursor: pointer;
}

.interlocuteur-table tbody tr:last-child td {
    border: none;
}

.interlocuteur-table thead tr:first-child {
    border-top: none;
}

.interlocuteur-table tr td {
    box-sizing: border-box;
    padding: 0 5px;
}

.radio {
    margin-top: 2px;
    margin-left: 32px;
}
.notFoundTd {
    text-align: center;
    height: 25vh;
}
.sortIcons {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    height: 20px;
    margin-right: 20px;
    float: right;
}
.pointer {
    cursor: pointer;
}
</style>
